import api from './api';

/**
 * Direct upload via backend (avoids GCS CORS). Backend streams the binary to GCS.
 * Returns the public URL of the uploaded object.
 */
export async function uploadImageViaDirect(
  comercioKey: string,
  file: File,
  prefix: string = 'orders'
): Promise<string> {
  // Use fetch instead of axios JSON client to send raw body
  const token = (typeof window !== 'undefined' ? localStorage.getItem('jwtToken') : null) || '';
  const qs = new URLSearchParams({
    filename: file.name || 'upload.bin',
    contentType: file.type || 'application/octet-stream',
    prefix,
  });
  const url = `${(api as any).defaults?.baseURL || ''}/comercios/${encodeURIComponent(comercioKey)}/uploads/direct?${qs.toString()}`;

  if (!token) {
    throw new Error('Authentication required for uploads.');
  }

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': file.type || 'application/octet-stream',
      'Authorization': `Bearer ${token}`,
    },
    body: file,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Direct upload failed: ${res.status} ${res.statusText} ${text}`);
  }

  const data = await res.json();
  return data.publicUrl as string;
}
/**
 * Upload via backend conversion pipeline.
 * Calls /uploads/images and returns the preferred WebP URL (optimized, or small, or original as fallback).
 */
export async function uploadImageViaImages(
  comercioKey: string,
  file: File
): Promise<string> {
  const token =
    (typeof window !== 'undefined' ? localStorage.getItem('jwtToken') : null) ||
    '';
  const qs = new URLSearchParams({
    filename: file.name || 'upload.bin',
    contentType: file.type || 'application/octet-stream',
  });
  const base =
    ((api as any).defaults?.baseURL as string | undefined)?.replace(/\/+$/, '') ||
    '';
  const url = `${base}/comercios/${encodeURIComponent(
    comercioKey
  )}/uploads/images?${qs.toString()}`;

  if (!token) {
    throw new Error('Authentication required for uploads.');
  }

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': file.type || 'application/octet-stream',
      Authorization: `Bearer ${token}`,
    },
    body: file,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(
      `Images upload failed: ${res.status} ${res.statusText} ${text}`
    );
  }

  const data: {
    original?: { url?: string };
    optimized?: { url?: string };
    small?: { url?: string };
  } = await res.json();

  const preferred =
    data?.optimized?.url || data?.small?.url || data?.original?.url;
  if (!preferred) {
    throw new Error('Images upload response missing URLs.');
  }
  return preferred;
}

/**
 * High-level helper for product images:
 * Route uploads through the backend conversion pipeline (/uploads/images)
 * and return the preferred WebP URL (optimized > small > original).
 * Note: prefix is ignored by the images pipeline (it always creates original/, optimized/, small/).
 */
export async function uploadImageToGcs(
  comercioKey: string,
  file: File,
  _prefix: string = 'orders'
): Promise<string> {
  return await uploadImageViaImages(comercioKey, file);
}